@file:OptIn(ExperimentalTime::class)

package com.alpha.showcase.common.cache.entity

import androidx.room3.ColumnInfo
import androidx.room3.Entity
import androidx.room3.Index
import androidx.room3.PrimaryKey
import com.alpha.showcase.common.utils.IMAGE_EXT_SUPPORT
import com.alpha.showcase.common.utils.VIDEO_EXT_SUPPORT
import com.alpha.showcase.common.utils.getExtension
import kotlinx.serialization.Serializable
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * 缓存项实体 - 存储单个文件/项目的缓存数据
 */
@Entity(
    tableName = "cached_items",
    indices = [
        Index(value = ["source_type", "source_key", "parent_path"], unique = false),
        Index(value = ["source_type", "source_key"], unique = false),
        Index(value = ["source_type", "source_key", "media_kind"], unique = false),
        // Matches the version-pinned paged/count/ordinal queries' full filter so
        // they never scan another version's (or another kind's) rows; the ORDER BY
        // still sorts the filtered rows, but the candidate set is exact.
        Index(value = ["source_type", "source_key", "sync_version", "media_kind"], unique = false),
        Index(value = ["created_at"], unique = false),
        Index(value = ["last_accessed"], unique = false),
        // Includes sync_version so a background re-sync's new-version rows do NOT
        // REPLACE the old committed-version rows: both coexist until the sync
        // finishes, then the old version is deleted (atomic switch). A path is still
        // unique WITHIN one sync_version.
        Index(value = ["source_type", "source_key", "path", "sync_version"], unique = true)
    ]
)
@Serializable
data class CachedItem(
    @PrimaryKey(autoGenerate = true)
    val id: Long = 0,

    /** 数据源类型 (ftp, smb, sftp, webdav, github, s3 等) */
    @ColumnInfo(name = "source_type")
    val sourceType: String,

    /** 数据源唯一标识键 */
    @ColumnInfo(name = "source_key")
    val sourceKey: String,

    /** 父路径 (用于构建层次结构) */
    @ColumnInfo(name = "parent_path")
    val parentPath: String,

    /** 项目名称 */
    @ColumnInfo(name = "name")
    val name: String,

    /** 完整路径/URL */
    @ColumnInfo(name = "path")
    val path: String,

    /** 是否为目录 */
    @ColumnInfo(name = "is_directory")
    val isDirectory: Boolean,

    /** 文件大小 */
    @ColumnInfo(name = "size")
    val size: Long,

    /** MIME类型/文件扩展名 */
    @ColumnInfo(name = "mime_type")
    val mimeType: String,

    /** 预计算的媒体类型: 0=other, 1=image, 2=video */
    @ColumnInfo(name = "media_kind", defaultValue = "0")
    val mediaKind: Int = CACHED_ITEM_MEDIA_KIND_OTHER,

    /** 修改时间戳 (毫秒) */
    @ColumnInfo(name = "modified_time")
    val modifiedTime: Long,

    /** 缓存创建时间 */
    @ColumnInfo(name = "created_at")
    val createdAt: Long = Clock.System.now().toEpochMilliseconds(),

    /** 最后访问时间 */
    @ColumnInfo(name = "last_accessed")
    val lastAccessed: Long = Clock.System.now().toEpochMilliseconds(),

    /** 是否为递归查询的结果 */
    @ColumnInfo(name = "is_recursive")
    val isRecursive: Boolean = false,

    /** 额外的元数据 (JSON格式存储) */
    @ColumnInfo(name = "metadata")
    val metadata: String? = null,

    /** 同步版本号，用于增量同步时标记当前批次 */
    @ColumnInfo(name = "sync_version", defaultValue = "0")
    val syncVersion: Long = 0
)

internal const val CACHED_ITEM_MEDIA_KIND_OTHER = 0
internal const val CACHED_ITEM_MEDIA_KIND_IMAGE = 1
internal const val CACHED_ITEM_MEDIA_KIND_VIDEO = 2

internal fun resolveCachedItemMediaKind(
    name: String,
    mimeType: String,
    isDirectory: Boolean,
): Int {
    if (isDirectory) return CACHED_ITEM_MEDIA_KIND_OTHER

    val normalizedMimeType = mimeType.lowercase()
    if (normalizedMimeType.startsWith("image/")) {
        return CACHED_ITEM_MEDIA_KIND_IMAGE
    }
    if (normalizedMimeType.startsWith("video/")) {
        return CACHED_ITEM_MEDIA_KIND_VIDEO
    }
    return when (name.getExtension().lowercase()) {
        in IMAGE_EXT_SUPPORT ->
            CACHED_ITEM_MEDIA_KIND_IMAGE
        in VIDEO_EXT_SUPPORT ->
            CACHED_ITEM_MEDIA_KIND_VIDEO
        else -> CACHED_ITEM_MEDIA_KIND_OTHER
    }
}
